import React, { Fragment } from 'react'
import styled from 'styled-components'

import { Box, Badge, Flex, Icon, Tooltip } from 'components'
import Text from 'components/Text'
import { inputHoverBgColor } from 'components/TextInput/SimpleTextInput'

import { LeadDetails } from 'types/global'
import { formatDatetime } from 'utils/datetime'
import { colors } from 'utils/colors'

interface Props {
  options: LeadDetails.ContactPerson[]
  value?: LeadDetails.Mobile
  onChange: (selected: LeadDetails.Mobile) => void
}

const MobileContactTable: React.FC<Props> = ({ options, value, onChange }) => (
  <Table>
    <tbody>
      <tr style={{ height: 0, border: 'none' }}>
        <th style={{ width: '50px' }}></th>
        <th style={{ width: 'auto' }}></th>
        <th style={{ width: '100%' }}></th>
        <th style={{ width: 'auto' }}></th>
        <th style={{ width: 'auto' }}></th>
      </tr>
      {options?.map((person) => (
        <Fragment key={person.id}>
          <tr data-row="header">
            <td colSpan={4}>
              <Flex>
                <Text fontWeight="medium">
                  {person.fname || person.lname ? `${person.fname ?? ''} ${person.lname ?? ''}` : 'no_name_2_lbl'}
                </Text>
                {person.isPrimary && <Badge color="orange" text="primary_contact_lbl" />}
              </Flex>
            </td>
          </tr>
          {person.mobile?.map((mobile) => (
            <tr key={mobile.id} onClick={() => onChange(mobile)} data-selected={value?.id === mobile.id}>
              <td>
                <Box visibility={mobile.isPrimary ? 'visible' : 'hidden'}>
                  <Icon icon="flag" color="yellow" />
                </Box>
              </td>
              <td>
                <Text color="grayShadeBlack2">{mobile.contactInfoType}</Text>
              </td>
              <td>
                <Text whiteSpace="nowrap">{mobile.displayValue}</Text>
              </td>
              <td>
                {mobile.lastCallTimestamp && (
                  <Flex gap="0" justifyContent="flex-end">
                    <Box py="0.5rem" maxWidth={180}>
                      <Text color="grayShadeBlack2" fontSize="10px" whiteSpace="nowrap" textAlign="right" pr="0.5rem">
                        {formatDatetime(mobile.lastCallTimestamp, 'PPPp')}
                      </Text>
                      <Text
                        color="grayShadeBlack2"
                        fontSize="10px"
                        textAlign="right"
                        whiteSpace="nowrap"
                        ellipsis
                        pr="0.5rem"
                      >
                        {mobile.lastCallStatus} | {mobile.lastCallOutcome}
                      </Text>
                    </Box>
                    <Tooltip
                      title={
                        <Box padding="0.5rem">
                          <Box borderBottom={`1px solid ${colors.gray2}`} paddingBottom="0.5rem" marginBottom="0.5rem">
                            <Text textAlign="left" color="gray2">
                              last_contacted_tooltip
                            </Text>
                          </Box>
                          <Text color="gray2" textAlign="left" pb="0.2rem">
                            {mobile.lastCallStatus} - {mobile.lastCallOutcome}
                          </Text>
                          <Text color="gray2" whiteSpace="nowrap" textAlign="left">
                            {formatDatetime(mobile.lastCallTimestamp, 'PPPp')}
                          </Text>
                        </Box>
                      }
                      position="bottomRight"
                      wordBreak="break-word"
                    >
                      <Icon color="gray5" icon={['far', 'circle-exclamation']} fontSize={12} />
                    </Tooltip>
                  </Flex>
                )}
              </td>
              <td>
                <Box visibility={value?.id === mobile.id ? 'visible' : 'hidden'}>
                  <Icon icon={['fas', 'check']} color="blue1" />
                </Box>
              </td>
            </tr>
          ))}
        </Fragment>
      ))}
    </tbody>
  </Table>
)

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  border: 1px solid ${(p) => p.theme.colors.defaultBorder};

  tr {
    height: 45px;

    &:not(:nth-child(2)) {
      border-top: 1px solid ${(p) => p.theme.colors.defaultBorder};
    }

    &[data-selected='true'] {
      background-color: ${(p) => p.theme.colors.blue5};
    }

    &:hover:not([data-row='header']) {
      background-color: ${inputHoverBgColor};
      cursor: pointer;
    }
  }

  td {
    vertical-align: middle;
    padding: 0 0.5rem;

    &:first-child {
      padding-left: 1rem;
    }

    &:last-child {
      padding-right: 1rem;
    }
  }
`

export default MobileContactTable
